import React, { useEffect, useState } from 'react';
import { User, Phone, Mail, Calendar, Clock, MessageCircle, Video } from 'lucide-react';
import { fetchAdvisorMeetingsData, fetchAdvisorDetailsData } from '../api';

const DEFAULT_ADVISOR = {
    advisor_id: 'default',
    advisor_name: 'Lisa Smith',
    advisor_title: 'Senior Wealth Advisor',
    advisor_img: null,
    advisor_phone: null,
    advisor_email: null,
    relationship_duration_years: 2,
    relationship_duration_months: 24,
    last_meeting: {
        date: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
        type: 'Quarterly Review',
        summary: 'You discussed portfolio performance and reviewed allocation strategy for the coming quarter.'
    },
    next_meeting: {
        date: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0],
        type: 'Portfolio Strategy Session',
        agenda: ['Portfolio rebalancing review', 'Tax planning opportunities', 'Goal progress update']
    },
    service_tier: 'Wealth Management'
};

const AdvisorCornerWidget = ({ userId, useCaseId }) => {
    const [advisorData, setAdvisorData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showNoAdvisorMessage, setShowNoAdvisorMessage] = useState(false);

    useEffect(() => {
        const fetchAdvisorData = async () => {
            try {
                setIsLoading(true);
                setShowNoAdvisorMessage(false);

                // Add timestamp to bust any caches
                const cacheBuster = Date.now();

                // Fetch meetings data from database via SQL agent
                const meetingsData = await fetchAdvisorMeetingsData(userId, useCaseId);

                if (!Array.isArray(meetingsData) || meetingsData.length === 0) {
                    setAdvisorData(DEFAULT_ADVISOR);
                    return;
                }


                // Separate past and future meetings
                const now = new Date();
                const pastMeetings = meetingsData.filter(m => {
                    const meetingDate = new Date(m.meeting_date);
                    return meetingDate < now || m.status?.toLowerCase() === 'completed';
                });
                const futureMeetings = meetingsData.filter(m => {
                    const meetingDate = new Date(m.meeting_date);
                    return meetingDate >= now && m.status?.toLowerCase() === 'scheduled';
                });

                // Get last (most recent past) and next (nearest future) meetings
                // pastMeetings are sorted ASC, so last item is most recent
                const lastMeeting = pastMeetings.length > 0 ? pastMeetings[pastMeetings.length - 1] : null;
                // futureMeetings are sorted ASC, so first item is nearest future
                const nextMeeting = futureMeetings.length > 0 ? futureMeetings[0] : null;

                // Get advisor details if we have an advisor_id
                const advisorId = meetingsData[0]?.advisor_id;
                let advisorDetails = null;

                if (advisorId) {
                    advisorDetails = await fetchAdvisorDetailsData(advisorId, userId, useCaseId);
                }

                // Calculate relationship duration from first meeting
                // Sort meetings by date (oldest first) to find the earliest meeting
                const sortedMeetings = [...meetingsData].sort((a, b) => {
                    const dateA = new Date(a.meeting_date || a.created_at);
                    const dateB = new Date(b.meeting_date || b.created_at);
                    return dateA - dateB;
                });

                let relationshipYears = 0;
                let relationshipMonths = 0;

                if (sortedMeetings.length > 0) {
                    const firstMeeting = sortedMeetings[0];
                    const firstMeetingDate = firstMeeting.meeting_date || firstMeeting.created_at;

                    if (firstMeetingDate) {
                        // Parse date - handle YYYY-MM-DD format without timezone issues
                        // If date is in YYYY-MM-DD format, parse it as local date
                        let firstDate;
                        if (firstMeetingDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                            // Pure date format - parse as local date at midnight
                            const [year, month, day] = firstMeetingDate.split('-').map(Number);
                            firstDate = new Date(year, month - 1, day);
                        } else {
                            // Has time component - use regular parsing
                            firstDate = new Date(firstMeetingDate);
                        }

                        const today = new Date();
                        // Set today to midnight for fair comparison
                        today.setHours(0, 0, 0, 0);

                        // Validate date is valid
                        if (!isNaN(firstDate.getTime()) && firstDate <= today) {
                            // Calculate months more accurately
                            const yearDiff = today.getFullYear() - firstDate.getFullYear();
                            const monthDiff = today.getMonth() - firstDate.getMonth();
                            const dayDiff = today.getDate() - firstDate.getDate();

                            relationshipMonths = yearDiff * 12 + monthDiff;

                            // Add partial month if we're past the same day of month
                            if (dayDiff >= 0) {
                                relationshipMonths += 1;
                            }

                            // Ensure at least 1 month for any relationship
                            relationshipMonths = Math.max(1, relationshipMonths);

                            // Convert to years (for display purposes)
                            relationshipYears = Math.round((relationshipMonths / 12) * 10) / 10;
                        }
                    }
                }

                // Parse meeting notes/agenda
                const parseAgenda = (notes) => {
                    if (!notes) return [];
                    // Try to split by common delimiters
                    const items = notes.split(/[,;|\n]/).map(item => item.trim()).filter(item => item.length > 0);
                    return items.length > 0 ? items : [notes];
                };

                // Personalize advisor notes from system perspective to customer perspective
                const personalizeNotes = (notes) => {
                    if (!notes) return notes;

                    let personalized = notes;

                    // Replace "Client" with "you" first
                    personalized = personalized.replace(/\bClient\s+/gi, 'you ');
                    personalized = personalized.replace(/\bThe client\s+/gi, 'you ');
                    personalized = personalized.replace(/\bclient\b/gi, 'you');

                    // Fix grammar issues that arise from client -> you conversion
                    personalized = personalized.replace(/\byou\s+wants\b/gi, 'you want');
                    personalized = personalized.replace(/\byou\s+has\b/gi, 'you have');
                    personalized = personalized.replace(/\byou\s+was\b/gi, 'you were');
                    personalized = personalized.replace(/\byou\s+is\b/gi, 'you are');
                    personalized = personalized.replace(/\byou\s+does\b/gi, 'you do');
                    personalized = personalized.replace(/\byou\s+goes\b/gi, 'you go');
                    personalized = personalized.replace(/\byou\s+needs\b/gi, 'you need');
                    personalized = personalized.replace(/\byou\s+satisfied\b/gi, 'you were satisfied');

                    // Capitalize first letter of sentence
                    personalized = personalized.charAt(0).toUpperCase() + personalized.slice(1);
                    personalized = personalized.replace(/\.\s+you\s/gi, '. You ');

                    // Clean up double spaces
                    personalized = personalized.replace(/\s+/g, ' ').trim();

                    return personalized;
                };

                // Use service_tier from database, with fallback derivation if not available
                const serviceTier = advisorDetails?.service_tier ||
                    (advisorDetails?.specialization?.includes('Private') ||
                        advisorDetails?.specialization?.includes('High Net Worth')
                        ? 'Private Wealth'
                        : 'Wealth Management');

                // Build advisor data object
                const transformedData = {
                    advisor_id: advisorId || 'N/A',
                    advisor_name: advisorDetails?.advisor_name || 'Lisa Smith',
                    advisor_title: advisorDetails?.designation || advisorDetails?.specialization || 'Wealth Advisor',
                    advisor_img: null,
                    advisor_phone: advisorDetails?.phone || null,
                    advisor_email: advisorDetails?.email || null,
                    relationship_duration_years: relationshipYears,
                    relationship_duration_months: relationshipMonths,
                    last_meeting: lastMeeting ? {
                        date: lastMeeting.meeting_date,
                        type: lastMeeting.meeting_type || lastMeeting.title || 'Meeting',
                        summary: personalizeNotes(lastMeeting.notes) || 'Meeting completed'
                    } : null,
                    next_meeting: nextMeeting ? {
                        date: nextMeeting.meeting_date,
                        type: nextMeeting.meeting_type || nextMeeting.title || 'Scheduled Meeting',
                        agenda: parseAgenda(nextMeeting.notes)
                    } : null,
                    service_tier: serviceTier
                };

                setAdvisorData(transformedData);
            } catch (error) {
                console.error('Error fetching advisor data:', error);
                setAdvisorData(DEFAULT_ADVISOR);
            } finally {
                setIsLoading(false);
            }
        };

        if (userId) {
            fetchAdvisorData();
        }
    }, [userId, useCaseId]);

    const getDaysUntilMeeting = (dateString) => {
        const meetingDate = new Date(dateString);
        const today = new Date();
        const diffTime = meetingDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const formatMeetingDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatRelationshipDuration = (years, months) => {
        // Use months directly if provided for more accuracy
        const totalMonths = months || Math.round(years * 12);

        if (totalMonths < 12) {
            return `${totalMonths} ${totalMonths === 1 ? 'month' : 'months'}`;
        } else if (totalMonths < 24) {
            return `${totalMonths} months`;
        } else {
            const yearValue = Math.round(totalMonths / 12 * 10) / 10;
            return `${yearValue} ${yearValue === 1 ? 'year' : 'years'}`;
        }
    };

    if (isLoading) {
        return (
            <div
                className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-md border border-slate-700/50 rounded-lg relative overflow-hidden h-full"
            >
                <div className="p-4 animate-pulse">
                    <div className="h-20 bg-slate-700/50 rounded-lg mb-3"></div>
                    <div className="h-16 bg-slate-700/50 rounded-lg mb-3"></div>
                    <div className="h-24 bg-slate-700/50 rounded-lg"></div>
                </div>
            </div>
        );
    }

    if (!advisorData) return null;

    const daysUntilMeeting = advisorData.next_meeting ? getDaysUntilMeeting(advisorData.next_meeting.date) : null;

    return (
        <div
            className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-md border border-slate-700/50 rounded-lg relative overflow-hidden h-full"
        >
            {/* Decorative elements */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl"></div>

            {/* Content */}
            <div className="relative z-10 p-4 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-medium text-white">{advisorData.advisor_name}</h2>
                    <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                        {advisorData.service_tier}
                    </span>
                </div>

                {/* Advisor Card */}
                <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-lg p-3 mb-3">
                    <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500/90 to-blue-500/90 flex items-center justify-center text-white font-semibold text-lg shadow-lg">
                            {advisorData.advisor_name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-slate-100">
                                {advisorData.advisor_name}
                            </h3>
                            <p className="text-xs text-slate-400 mb-2">
                                {advisorData.advisor_title}
                            </p>
                            <div className="flex gap-2">
                                <button className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                                    <Phone size={12} />
                                    Call
                                </button>
                                <button className="flex items-center gap-1 text-xs text-primary hover:text-primaryLight transition-colors">
                                    <MessageCircle size={12} />
                                    Message
                                </button>
                                <button className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                                    <Video size={12} />
                                    Video
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Next Meeting */}
                {advisorData.next_meeting && (
                    <div className="bg-slate-800/40 backdrop-blur-sm border border-emerald-500/30 rounded-lg p-3 mb-3">
                        <div className="flex items-start gap-2 mb-2">
                            <Calendar size={16} className="text-emerald-400 mt-0.5" />
                            <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                    <h4 className="text-xs font-semibold text-slate-100">
                                        {advisorData.next_meeting.type}
                                    </h4>
                                    <span className="text-xs text-emerald-400 font-medium">
                                        {daysUntilMeeting === 0 ? 'Today' : daysUntilMeeting === 1 ? 'Tomorrow' : `In ${daysUntilMeeting} days`}
                                    </span>
                                </div>
                                <p className="text-xs text-slate-300 mb-2">
                                    {formatMeetingDate(advisorData.next_meeting.date)}
                                </p>
                                <button className="w-full text-[11px] text-left px-3 py-2 bg-slate-800/60 hover:bg-slate-700/60 border border-slate-600/50 hover:border-emerald-500/50 rounded text-slate-300 hover:text-emerald-300 transition-all flex items-center gap-2">
                                    <MessageCircle size={12} />
                                    <span>Add topics to discuss with your advisor</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Last Meeting Summary */}
                {advisorData.last_meeting && (
                    <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-lg p-3 flex-1">
                        <div className="flex items-start gap-2">
                            <Clock size={14} className="text-slate-400 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <h4 className="text-xs font-semibold text-slate-200 mb-1">
                                    Last Meeting: {advisorData.last_meeting.type}
                                </h4>
                                <p className="text-[10px] text-slate-400 mb-1">
                                    {formatMeetingDate(advisorData.last_meeting.date)}
                                </p>
                                <p className="text-xs text-slate-300 leading-relaxed">
                                    {advisorData.last_meeting.summary}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Relationship Duration */}
                <div className="mt-3 pt-3 border-t border-slate-700/50 text-center">
                    <p className="text-xs text-slate-400">
                        Working together for <span className="text-emerald-400 font-semibold">{formatRelationshipDuration(advisorData.relationship_duration_years, advisorData.relationship_duration_months)}</span>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AdvisorCornerWidget;
